import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { notification } from 'antd';
import {
  useGetConsistencyCollectionsQuery,
  useCreateConsistencyCollectionMutation,
  useUpdateConsistencyCollectionMutation,
  useDeleteConsistencyCollectionMutation,
  useUpdateConsistencySchemaFilterMutation,
  useDeleteConsistencySchemaFilterMutation,
  useDeleteConsistencyRuleMutation,
  useUpdateConsistencyRuleMutation,
  GQLOperations,
} from 'types/dt-graphql-apollo-v2';
import {
  mapCollectionToView,
  mapSchemaFilterToView,
  mapRuleToView,
  ConsistencyCollectionView,
  ConsistencySchemaFilterView,
  ConsistencyRuleView,
} from '../types';
import { toggleDisabledTag } from '../helpers';

const REFETCH_CONFIG = {
  refetchQueries: [GQLOperations.Query.GetConsistencyCollections],
};

const POLL_INTERVAL_MS = 5000;

export const useDataConsistency = (
  digitalTwinId: string,
  tenant: string,
  schemaNameById: Record<string, string> = {},
  polling: boolean = false
) => {
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>();
  const [search, setSearch] = useState('');

  const { data, loading, error } = useGetConsistencyCollectionsQuery({
    variables: { projectId: digitalTwinId },
    skip: !digitalTwinId,
    pollInterval: polling ? POLL_INTERVAL_MS : 0,
  });

  const [createMutation] =
    useCreateConsistencyCollectionMutation(REFETCH_CONFIG);
  const [updateMutation] =
    useUpdateConsistencyCollectionMutation(REFETCH_CONFIG);
  const [deleteMutation] =
    useDeleteConsistencyCollectionMutation(REFETCH_CONFIG);
  const [updateSchemaFilterMutation] =
    useUpdateConsistencySchemaFilterMutation(REFETCH_CONFIG);
  const [deleteSchemaFilterMutation] =
    useDeleteConsistencySchemaFilterMutation(REFETCH_CONFIG);
  const [deleteRuleMutation] = useDeleteConsistencyRuleMutation(REFETCH_CONFIG);
  const [updateRuleMutation] = useUpdateConsistencyRuleMutation(REFETCH_CONFIG);
  const [togglingDisableRuleId, setTogglingDisableRuleId] = useState<
    string | null
  >(null);

  const rawCollections = useMemo(
    () =>
      (data?.listConsistencyCollection?.items ?? []).filter(
        (c): c is NonNullable<typeof c> => c !== null
      ),
    [data]
  );

  const collections: ConsistencyCollectionView[] = useMemo(
    () => rawCollections.map(mapCollectionToView),
    [rawCollections]
  );

  // Auto-select first collection when none is selected
  const effectiveSelectedId =
    selectedCollectionId ?? collections[0]?.externalId;

  const selectedCollection = collections.find(
    (c) => c.externalId === effectiveSelectedId
  );

  const selectedRawCollection = rawCollections.find(
    (c) => c.externalId === effectiveSelectedId
  );

  const collectionSchemaFilters: ConsistencySchemaFilterView[] = useMemo(
    () =>
      (selectedRawCollection?.schemaFilters?.items ?? [])
        .filter((sf): sf is NonNullable<typeof sf> => sf !== null)
        .map((sf) => mapSchemaFilterToView(sf, schemaNameById)),
    [selectedRawCollection, schemaNameById]
  );

  const filteredSchemaFilters = useMemo(
    () =>
      collectionSchemaFilters.filter(
        (sf) =>
          search === '' ||
          sf.name.toLowerCase().includes(search.toLowerCase()) ||
          sf.schemaType.toLowerCase().includes(search.toLowerCase())
      ),
    [collectionSchemaFilters, search]
  );

  const comparisonRules: ConsistencyRuleView[] = useMemo(
    () =>
      (selectedRawCollection?.rules?.items ?? [])
        .filter((r): r is NonNullable<typeof r> => r !== null)
        .map(mapRuleToView),
    [selectedRawCollection]
  );

  const filteredComparisonRules = useMemo(
    () =>
      comparisonRules.filter(
        (r) =>
          search === '' || r.name.toLowerCase().includes(search.toLowerCase())
      ),
    [comparisonRules, search]
  );

  const createCollection = async (params: {
    name: string;
    description: string;
    labels: string[];
  }) => {
    const id = uuidv4();
    await createMutation({
      variables: {
        id,
        name: params.name,
        description: params.description,
        tenant,
        projectId: digitalTwinId,
        labels: params.labels,
        digitalTwinId,
      },
    });
    setSelectedCollectionId(id);
  };

  const deleteCollection = async () => {
    if (!effectiveSelectedId) return;
    await deleteMutation({ variables: { id: effectiveSelectedId } });
    setSelectedCollectionId(undefined);
  };

  const deleteCollectionById = async (id: string) => {
    await deleteMutation({ variables: { id } });
    if (id === effectiveSelectedId) {
      setSelectedCollectionId(undefined);
    }
  };

  const updateCollection = async (
    id: string,
    updates: { name: string; description: string; labels: string[] }
  ) => {
    await updateMutation({ variables: { id, ...updates } });
  };

  const updateSchemaFilter = async (
    id: string,
    updates: {
      name: string;
      description: string;
      labels: string[];
      schemaType: string;
      schemaCollectionId: string;
      filters: string;
    }
  ) => {
    await updateSchemaFilterMutation({ variables: { id, ...updates } });
  };

  const deleteSchemaFilter = async (id: string) => {
    try {
      await deleteSchemaFilterMutation({ variables: { id } });
    } catch (err: unknown) {
      notification.error({
        message: 'Could not delete schema filter',
        description: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  };

  const toggleDisableRule = async (id: string, disable: boolean) => {
    const current = comparisonRules.find((r) => r.externalId === id);
    if (!current) return;
    const tags = toggleDisabledTag(current.tags, disable);
    setTogglingDisableRuleId(id);
    try {
      await updateRuleMutation({ variables: { id, tags } });
    } catch (err: unknown) {
      notification.error({
        message: "Couldn't enable/disable rule.",
        description: err instanceof Error ? err.message : String(err),
      });
      throw err;
    } finally {
      setTogglingDisableRuleId(null);
    }
  };

  const deleteRule = async (id: string) => {
    try {
      await deleteRuleMutation({ variables: { id } });
    } catch (err: unknown) {
      notification.error({
        message: 'Could not delete comparison rule',
        description: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  };

  return {
    collections,
    selectedCollection,
    selectedCollectionId: effectiveSelectedId,
    search,
    loading,
    error,
    collectionSchemaFilters,
    filteredSchemaFilters,
    comparisonRules,
    filteredComparisonRules,
    setSelectedCollectionId,
    setSearch,
    createCollection,
    deleteCollection,
    deleteCollectionById,
    updateCollection,
    updateSchemaFilter,
    deleteSchemaFilter,
    deleteRule,
    toggleDisableRule,
    togglingDisableRuleId,
  };
};
